import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import {
  Alert,
  Button,
  ButtonGroup,
  ButtonToolbar,
  Col,
  Row,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import type { ReportBatch } from "../../../api/src/reports/reports.service";
import ActionButton from "../components/ActionButton";
import TabContainerWithTabs from "../components/TabContainer";
import { dateFilterFn, DateRangeFilter } from "../components/Table/filters";
import { Table, useTable } from "../components/Table/Table";
import Loading from "../Loading";
import { useQueryReportBatches } from "./lib";
import ReportsNav from "./ReportsNav";

const ReportBatches = () => {
  const columnHelper = useMemo(() => createColumnHelper<ReportBatch>(), []);

  const columns = useMemo(
    () => [
      columnHelper.accessor("name", {
        cell: (info) => (
          <Link to={`/reports/${info.row.original.id}`}>{info.getValue()}</Link>
        ),
        header: () => "Name",
      }),
      columnHelper.accessor("reportDate", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "Report Date",
        meta: {
          filterComponent: (props) => (
            <DateRangeFilter {...props} name="reportDate" />
          ),
        },
        filterFn: dateFilterFn,
      }),
      columnHelper.accessor("startDate", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "Start Date",
        meta: {
          filterComponent: (props) => (
            <DateRangeFilter {...props} name="startDate" />
          ),
        },
        filterFn: dateFilterFn,
      }),
      columnHelper.accessor("endDate", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "End Date",
        meta: {
          filterComponent: (props) => (
            <DateRangeFilter {...props} name="endDate" />
          ),
        },
        filterFn: dateFilterFn,
      }),
      columnHelper.accessor("createdTime", {
        cell: (info) => info.getValue().toLocaleDateString(),
        header: () => "Created",
        enableColumnFilter: false,
      }),
    ],
    [columnHelper],
  );

  const {
    isPending,
    isError,
    data: reportBatches,
    retry,
  } = useQueryReportBatches();

  const { table } = useTable({
    columns,
    data: reportBatches ?? [],
    getRowId: (row) => row.id.toString(),
    initialState: {
      sorting: [{ id: "reportDate", desc: true }],
    },
  });

  return (
    <TabContainerWithTabs tabs={ReportsNav}>
      <Row>
        <Col xl={12}>
          <ButtonToolbar className="mb-3">
            <ButtonGroup className="me-3">
              <ActionButton
                variant="secondary"
                label="Run Reports"
                icon="/icons/new.svg"
                className="btn-action_secondary_wide"
                as={Link}
                to="/reports/batches/new?type=custom"
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>
          {isPending ? (
            <Loading message="reports" />
          ) : isError ? (
            <Alert variant="danger">
              An error occurred{" "}
              <Button variant="outline-danger" size="sm" onClick={retry}>
                Try again
              </Button>
            </Alert>
          ) : (
            <Table table={table} />
          )}
        </Col>
      </Row>
    </TabContainerWithTabs>
  );
};

export default ReportBatches;
